import { defineStore } from 'pinia'
import { useAuthStore, useNotificationCenterStore, useProgrammeStore, useUserStore } from '@/stores'
import {
  PushNotifications,
  type ActionPerformed,
  type PushNotificationSchema
} from '@capacitor/push-notifications'
import { Device } from '@capacitor/device'
import { App } from '@capacitor/app'
import { factory } from '../../../../factory-api/factory'
import type { NotificationMessage, PushToken } from '@/generated-api'

export const usePushNotificationStore = defineStore('PushNotificationStore', () => {
  // State
  const initialized = ref(false)
  const hasActiveListeners = ref(false)
  const notificationStore = useNotificationCenterStore()
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const programmeStore = useProgrammeStore()
  const deviceId = ref<string>()
  const appId = ref<string>()
  const pushToken = ref<string>()
  const currentPushNotification = ref<PushNotificationSchema>()
  const notificationMessage = ref<NotificationMessage>()
  const currentTokenData = ref<PushToken>()
  const showDialog = ref(<boolean>false)

  // Actions
  const initializePushNotifications = async () => {
    if (initialized.value) {
      return
    }

    try {
      await getDeviceId()
      await getAppId()
      await registerPushNotifications()
      await addListeners()
      initialized.value = true
    } catch (error) {
      console.error('Failed to initialize notifications:', error)
    }
  }

  // device ID
  const getDeviceId = async () => {
    const info = await Device.getId()
    deviceId.value = info.identifier
    console.log('Device.getId() ', info)
    return deviceId.value
  }

  // app ID
  const getAppId = async () => {
    const info = await App.getInfo()
    appId.value = info.id
    console.log('App.getInfo() ', info)
    return appId.value
  }

  const addListeners = async () => {
    // When FCM registers the device successfully, it returns a token
    // This token is unique to the device/app and is used to send notifications to this specific device
    await PushNotifications.addListener('registration', async (token) => {
      pushToken.value = token.value
      console.info('Registration token: ', token.value)
    })

    // If there's an error during FCM registration (e.g. Google Play Services not available)
    // This event will fire with the error details
    await PushNotifications.addListener('registrationError', (err) => {
      console.error('Registration error: ', err.error)
    })

    // When a push notification is received while the app is in foreground
    // The notification object contains the message payload and any custom data
    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        // Access notification properties to check if it's silent
        const isSilent = !notification.title && !notification.body
        currentPushNotification.value = notification
        notificationMessage.value = notification.data as NotificationMessage
        console.log('Push notification received: ', notification, 'Is silent:', isSilent)
      }
    )

    // When user interacts with a notification (taps it)
    // actionId: the action user took (e.g. tapped notification or specific action button)
    // inputValue: any user input if the notification had an input field
    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async (action: ActionPerformed) => {
        currentPushNotification.value = action.notification
        notificationMessage.value = action.notification.data as NotificationMessage
        // Check if user is either authenticated OR has selected a programme
        // This ensures we only handle notifications when user has proper access
        if (authStore.isAuthenticated) {
          console.log('User is authenticated, ActionPerformed: ', action)
          if (
            programmeStore.selectedProgramme?.programmeId !== notificationMessage.value.programmeId
          ) {
            // The user is not in the right programme
            console.log('User is not in the right programme, ActionPerformed: ', action)
            showDialog.value = true
          } else {
            notificationStore.goToLink(notificationMessage.value)
          }
        } else if (action.actionId === 'tap') {
          // If user is not authenticated but the notification was tapped
          // This allows handling specific tap actions even when not logged in
          console.log('User is not authenticated but notification was tapped ', action)
        }
      }
    )
    hasActiveListeners.value = true
  }

  const savePushToken = async (token: string) => {
    try {
      const notificationApi = await factory.getNotificationCenterApi()
      const currentMember = userStore.member

      // Save the push token
      await notificationApi.savePushToken({
        deviceId: deviceId.value || 'undefined',
        appId: appId.value || 'undefined',
        pushTokenSaveRequest: {
          token: token,
          candidateId: currentMember?.id || 'undefined'
        }
      })

      console.log('Successfully saved push token')
      pushToken.value = token
    } catch (error) {
      console.error('Failed to save push token:', error)
    }
  }

  const getCurrentTokenData = async () => {
    const notificationApi = await factory.getNotificationCenterApi()
    currentTokenData.value = await notificationApi.getPushToken({
      appId: appId.value || 'undefined',
      deviceId: deviceId.value || 'undefined'
    })
  }

  const checkTokenStatus = async () => {
    try {
      const currentMember = userStore.member
      if (!currentMember?.id) {
        console.log('No memberId available - cannot check token status')
        return false
      }

      if (!pushToken.value) {
        console.log('No push token available - cannot check token status')
        return false
      }

      // Get current token data from server
      await getCurrentTokenData()

      // no token data from the server
      // candidateId is different (user changed)
      // the token itself is different
      if (
        !currentTokenData.value ||
        currentTokenData.value.candidateId !== currentMember.id ||
        currentTokenData.value.token !== pushToken.value
      ) {
        console.log('Token needs updating')
        return true
      }

      console.log('Token is up to date')
      return false
    } catch (error) {
      console.error('Error checking token status:', error)
      return true
    }
  }

  const resaveTokenAfterLogin = async () => {
    if (!authStore.isAuthenticated) {
      console.log('User not authenticated, cannot save push token')
      return false
    }

    const shouldSaveToken = await checkTokenStatus()

    if (shouldSaveToken) {
      console.log('Saving push token after login')
      await savePushToken(pushToken.value!)
      return true
    }

    return false
  }

  const registerPushNotifications = async () => {
    let permStatus = await PushNotifications.checkPermissions()

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions()
    }

    if (permStatus.receive !== 'granted') {
      throw new Error('User denied permissions!')
    }

    await PushNotifications.register()
  }

  const resetPushNotificationPermission = async () => {
    // Remove all listeners
    await PushNotifications.removeAllListeners()
    // Unregister the device
    await PushNotifications.unregister()
    // Reset internal states
    initialized.value = false
    hasActiveListeners.value = false
  }

  const closeDialog = () => {
    showDialog.value = false
  }

  return {
    initialized,
    pushToken,
    initializePushNotifications,
    resetPushNotificationPermission,
    getDeviceId,
    getAppId,
    resaveTokenAfterLogin,
    notificationMessage,
    showDialog,
    closeDialog
  }
})
